import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Trash2, PlusCircle, Upload, CheckCircle2, Package, ShoppingBag, Wallet, TrendingUp, Instagram, Facebook, Globe, Bell, Pencil, Tag, Truck, RotateCw, Clock } from "lucide-react";
import { toast } from "sonner";
import OrderStagePill from "@/components/OrderStagePill";
import ShipmentControls from "@/components/ShipmentControls";
import VendorPayouts from "@/components/VendorPayouts";
import VendorReviewsTab from "@/components/VendorReviewsTab";
import VendorReturnsTab from "@/components/returns/VendorReturnsTab";
import VerifiedBadge from "@/components/VerifiedBadge";
import BoostProductDialog from "@/components/BoostProductDialog";
import WalletRechargeDialog from "@/components/WalletRechargeDialog";
import SubmitToDropButton from "@/components/SubmitToDropButton";
import VendorLiveTab from "@/components/VendorLiveTab";

const FALLBACK_CATS = [
  { slug: "chaniya-choli", name: "Chaniya Choli", gst_default: 5 },
  { slug: "gift-articles", name: "Gift Articles", gst_default: 18 },
  { slug: "accessories", name: "Accessories", gst_default: 12 },
  { slug: "womens-fashion", name: "Women's Fashion", gst_default: 5 },
  { slug: "mens-fashion", name: "Men's Fashion", gst_default: 5 },
  { slug: "kids", name: "Kids", gst_default: 5 },
  { slug: "trending", name: "Trending", gst_default: 18 },
];

const STATUS_OPTIONS = [
  { v: "confirmed", label: "Confirmed" },
  { v: "packed", label: "Packed" },
  { v: "shipped", label: "Shipped" },
  { v: "delivered", label: "Delivered" },
  { v: "cancelled", label: "Cancelled" },
];

const emptyProduct = { name: "", description: "", mrp: "", discount: "", category: "womens-fashion", image_base64: "", images: [], stock: 10, duration_hours: 48, sizes: [], color: "", material: "", specifications: "", gst_slab: 5, hsn_code: "", packaging_charge: 0, weight_kg: "", length_cm: "", breadth_cm: "", height_cm: "", delivery_within_city: true, delivery_within_state: true, delivery_out_of_state: true, cod_available: true };

const PRESET_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "Free"];

// Indian GST slab suggestion rules for CityShor categories.
// Clothing / Footwear / Kids: <₹1000 → 5%, ≥₹1000 → 12%.
// Accessories → 12%. Handmade / Handicraft → 5%. Toys → 12%. Others → category default.
function suggestGstSlab(categorySlug, mrpValue, catsList) {
  const catObj = (catsList || []).find((c) => c.slug === categorySlug);
  const catName = (catObj?.name || categorySlug || "").toLowerCase();
  const slug = (categorySlug || "").toLowerCase();
  const key = `${slug} ${catName}`;
  const mrp = Number(mrpValue) || 0;
  const label = catObj?.name || "this category";

  if (/(kid|chaniya|choli|fashion|clothing|apparel|dress|footwear|shoe|saree|kurta|ethnic)/.test(key)) {
    if (mrp > 0 && mrp < 1000) return { slab: 5, reason: `Suggested 5% — ${label} priced below ₹1000.` };
    if (mrp >= 1000) return { slab: 12, reason: `Suggested 12% — ${label} priced ₹1000 or above.` };
    return { slab: 5, reason: `Suggested 5% by default. Rate becomes 12% once MRP reaches ₹1000 or more.` };
  }
  if (/(accessor|jewel|bag|belt|watch)/.test(key)) {
    return { slab: 12, reason: `Suggested 12% — accessories attract a flat 12% GST.` };
  }
  if (/(handmade|handicraft|artisan|handloom)/.test(key)) {
    return { slab: 5, reason: `Suggested 5% — handmade and handicraft goods.` };
  }
  if (/(toy)/.test(key)) {
    return { slab: 12, reason: `Suggested 12% — toys.` };
  }
  const fallback = catObj?.gst ?? 18;
  return { slab: fallback, reason: `Suggested ${fallback}% based on the ${label} default rate.` };
}

export default function VendorDashboard() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [profile, setProfile] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [cats, setCats] = useState(FALLBACK_CATS);
  const CATS = cats.map((c) => ({ slug: c.slug, name: c.name, gst: c.gst_default ?? 18 }));
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [agreeForm, setAgreeForm] = useState({ agreement: false, payout: false });
  const [agreeBusy, setAgreeBusy] = useState(false);
  const [form, setForm] = useState(emptyProduct);
  const [gstOverridden, setGstOverridden] = useState(false);
  const gstSuggestion = suggestGstSlab(form.category, form.mrp, CATS);

  // Auto-apply the GST suggestion when category or MRP changes, unless the vendor manually overrode it.
  useEffect(() => {
    if (gstOverridden) return;
    if (form.gst_slab !== gstSuggestion.slab) {
      setForm((f) => ({ ...f, gst_slab: gstSuggestion.slab }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.category, form.mrp, gstOverridden]);
  const [profileForm, setProfileForm] = useState({ phone: "", whatsapp: "", instagram_handle: "", facebook_handle: "", website_url: "", business_address: "" });
  const [offers, setOffers] = useState([]);
  const [renewals, setRenewals] = useState({ used_today: 0, limit_per_day: 5, remaining: 5, resets_at: null });
  const [wallet, setWallet] = useState({ balance: 0, transactions: [] });
  const [myBoosts, setMyBoosts] = useState([]);
  const activeBoostByProduct = Object.fromEntries(
    myBoosts
      .filter((b) => b.status === "active" && new Date(b.end_date) >= new Date() && new Date(b.start_date) <= new Date())
      .map((b) => [b.product_id, b])
  );
  const [offerOpen, setOfferOpen] = useState(false);
  const emptyOffer = { code: "", discount_percent: 10, free_delivery: false, product_ids: [], starts_at: new Date().toISOString().slice(0, 10), ends_at: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10), active: true };
  const [offerForm, setOfferForm] = useState(emptyOffer);
  const [editingOfferId, setEditingOfferId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return nav("/login");
    if (user.role !== "vendor" && user.role !== "admin") return nav("/");
    reload();
  }, [user, authLoading]);

  // ---- Notifications: background poll + sound alert on new arrivals ----
  const [lastViewedNotifAt, setLastViewedNotifAt] = useState(
    () => localStorage.getItem("vendor_notif_last_viewed") || new Date(0).toISOString()
  );
  const knownNotifIdsRef = useRef(null);
  const unreadNotifCount = notifs.filter((n) => new Date(n.created_at) > new Date(lastViewedNotifAt)).length;

  const playNotificationSound = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(); osc.stop(ctx.currentTime + 0.5);
      setTimeout(() => { osc.frequency.value = 1100; }, 150);
    } catch { /* audio not available in this browser/context */ }
  };

  useEffect(() => {
    if (!user || (user.role !== "vendor" && user.role !== "admin")) return;
    const poll = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (knownNotifIdsRef.current) {
          const isNew = data.some((n) => !knownNotifIdsRef.current.has(n.id));
          if (isNew) playNotificationSound();
        }
        knownNotifIdsRef.current = new Set(data.map((n) => n.id));
        setNotifs(data);
      } catch { /* skip a beat, try again next interval */ }
    };
    const id = setInterval(poll, 20000);
    return () => clearInterval(id);
  }, [user]);

  useEffect(() => {
    if (tab === "notif") {
      const now = new Date().toISOString();
      setLastViewedNotifAt(now);
      localStorage.setItem("vendor_notif_last_viewed", now);
    }
  }, [tab]);

  const reload = async () => {
    const [a, p, o, pr, n, of, rn, cs, w, mb] = await Promise.all([
      api.get("/exhibitor/analytics"),
      api.get("/products-mine"),
      api.get("/exhibitor/orders"),
      api.get("/exhibitor/profile"),
      api.get("/notifications"),
      api.get("/exhibitor/offers"),
      api.get("/exhibitor/renewals-status"),
      api.get("/categories"),
      api.get("/exhibitor/wallet").catch(() => ({ data: { balance: 0, transactions: [] } })),
      api.get("/exhibitor/boosts").catch(() => ({ data: [] })),
    ]);
    setAnalytics(a.data); setProducts(p.data); setOrders(o.data);
    setProfile(pr.data); setNotifs(n.data); setOffers(of.data);
    setRenewals(rn.data);
    setWallet(w.data || { balance: 0, transactions: [] });
    setMyBoosts(mb.data || []);
    if (Array.isArray(cs.data) && cs.data.length) setCats(cs.data);
    const k = pr.data.kyc || {};
    setProfileForm({
      phone: pr.data.user?.phone || k.phone || "",
      whatsapp: pr.data.user?.whatsapp || "",
      instagram_handle: k.instagram_handle || "",
      facebook_handle: k.facebook_handle || "",
      website_url: k.website_url || "",
      business_address: k.business_address || "",
    });
  };

  const renewProduct = async (id) => {
    try {
      const { data } = await api.post(`/exhibitor/products/${id}/renew`);
      if (data?.status) setRenewals(data.status);
      toast.success("Product is live again");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not renew this product");
    }
  };

  const openNewOffer = (preselectProductIds = []) => {
    setEditingOfferId(null);
    setOfferForm({ ...emptyOffer, product_ids: Array.isArray(preselectProductIds) ? preselectProductIds : [] });
    setOfferOpen(true);
  };

  const openEditOffer = (o) => {
    setEditingOfferId(o.id);
    setOfferForm({
      code: o.code,
      discount_percent: o.discount_percent,
      free_delivery: !!o.free_delivery,
      product_ids: o.product_ids || [],
      starts_at: (o.starts_at || "").slice(0, 10),
      ends_at: (o.ends_at || "").slice(0, 10),
      active: !!o.active,
    });
    setOfferOpen(true);
  };

  const toggleOfferProduct = (pid) => {
    setOfferForm((prev) => {
      const has = prev.product_ids.includes(pid);
      return { ...prev, product_ids: has ? prev.product_ids.filter((x) => x !== pid) : [...prev.product_ids, pid] };
    });
  };

  const saveOffer = async () => {
    const pct = Number(offerForm.discount_percent);
    if (!offerForm.code.trim()) return toast.error("Enter a discount code");
    if (pct < 5 || pct > 50) return toast.error("Discount must be between 5% and 50%");
    if (!offerForm.product_ids.length) return toast.error("Pick at least one product");
    if (!offerForm.starts_at || !offerForm.ends_at) return toast.error("Set start and end dates");
    setBusy(true);
    try {
      const payload = {
        code: offerForm.code.toUpperCase(),
        discount_percent: pct,
        free_delivery: !!offerForm.free_delivery,
        product_ids: offerForm.product_ids,
        starts_at: new Date(offerForm.starts_at + "T00:00:00").toISOString(),
        ends_at: new Date(offerForm.ends_at + "T23:59:59").toISOString(),
        active: !!offerForm.active,
      };
      if (editingOfferId) {
        await api.patch(`/exhibitor/offers/${editingOfferId}`, payload);
        toast.success("Offer updated");
      } else {
        await api.post("/exhibitor/offers", payload);
        toast.success("Offer created");
      }
      setOfferOpen(false); setEditingOfferId(null); setOfferForm(emptyOffer); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save offer"); }
    finally { setBusy(false); }
  };

  const toggleOfferActive = async (o) => {
    try {
      await api.patch(`/exhibitor/offers/${o.id}`, { active: !o.active });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteOffer = async (id) => {
    if (!window.confirm("Delete this offer?")) return;
    await api.delete(`/exhibitor/offers/${id}`);
    toast.success("Offer removed");
    reload();
  };

  const MAX_IMG_DIM = 1600; // server re-fits every image to the 1200 x 1500 storefront frame
  const MAX_IMG_BYTES = 900 * 1024; // keep each compressed image under ~900KB

  const compressImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the selected image"));
    reader.onload = (ev) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Unsupported image format"));
      img.onload = () => {
        const scale = Math.min(1, MAX_IMG_DIM / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        let quality = 0.85;
        let out = canvas.toDataURL("image/jpeg", quality);
        // Step quality down until the payload is comfortably small.
        while (out.length * 0.75 > MAX_IMG_BYTES && quality > 0.4) {
          quality -= 0.15;
          out = canvas.toDataURL("image/jpeg", quality);
        }
        resolve(out);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });

  const formatApiError = (e, fallback = "Failed") => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string" && d.trim()) return d;
    if (Array.isArray(d) && d.length) {
      return d.map((x) => (typeof x === "string" ? x : `${(x.loc || []).join(".") || "field"}: ${x.msg || "invalid"}`)).join("; ");
    }
    if (e?.response?.status === 413) return "Upload too large — please use smaller photos and try again";
    if (e?.code === "ERR_NETWORK" || !e?.response) return "Network error — the upload may be too large or your connection dropped. Try smaller photos.";
    return fallback;
  };

  const onFile = (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    const remaining = 6 - (form.images?.length || 0);
    const toRead = files.slice(0, remaining);
    if (files.length > remaining) toast.error(`Max 6 images. Added the first ${remaining}.`);
    toRead.forEach((f) => {
      compressImage(f)
        .then((dataUrl) => setForm((prev) => ({
          ...prev,
          images: [...(prev.images || []), dataUrl].slice(0, 6),
          image_base64: prev.image_base64 || dataUrl,
        })))
        .catch((err) => toast.error(err.message || "Could not process image"));
    });
    e.target.value = "";
  };

  const removeImage = (idx) => {
    setForm((prev) => {
      const next = (prev.images || []).filter((_, i) => i !== idx);
      return { ...prev, images: next, image_base64: next[0] || "" };
    });
  };

  const submitProduct = async () => {
    const gallery = (form.images && form.images.length ? form.images : (form.image_base64 ? [form.image_base64] : []));
    if (gallery.length === 0) return toast.error("Please upload at least one product image (up to 6)");
    if (!form.name?.trim()) return toast.error("Please enter the product name");
    const hsn = (form.hsn_code || "").trim();
    if (hsn && !/^\d{4,8}$/.test(hsn)) return toast.error("HSN Code must be 4 to 8 digits");
    if (![0, 5, 12, 18, 28].includes(Number(form.gst_slab))) return toast.error("Please select a GST Rate");
    const mrp = Number(form.mrp);
    if (!mrp || mrp <= 0) return toast.error("Please enter a valid MRP");
    let discount = Number(form.discount || 0);
    if (discount < 0) discount = 0;
    if (discount > 100) discount = 100;
    const price = Math.round((mrp - (mrp * discount) / 100) * 100) / 100;
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        category: form.category,
        image_base64: gallery[0],
        images: gallery,
        stock: Number(form.stock),
        duration_hours: Number(form.duration_hours),
        price,
        original_price: mrp,
        gst_slab: Number(form.gst_slab),
        hsn_code: hsn,
        packaging_charge: Number(form.packaging_charge || 0),
        weight_kg: form.weight_kg === "" ? null : Number(form.weight_kg),
        length_cm: form.length_cm === "" ? null : Number(form.length_cm),
        breadth_cm: form.breadth_cm === "" ? null : Number(form.breadth_cm),
        height_cm: form.height_cm === "" ? null : Number(form.height_cm),
        size: form.sizes && form.sizes.length ? form.sizes.join(" / ") : "",
        sizes: form.sizes || [],
        color: form.color,
        material: form.material,
        specifications: form.specifications,
        delivery_within_city: !!form.delivery_within_city,
        delivery_within_state: !!form.delivery_within_state,
        delivery_out_of_state: !!form.delivery_out_of_state,
        cod_available: !!form.cod_available,
      };
      // Preflight: warn early if the JSON body is likely to exceed the proxy limit.
      const payloadBytes = new Blob([JSON.stringify(payload)]).size;
      if (payloadBytes > 4 * 1024 * 1024) {
        toast.error("Images are too large to upload together. Remove one or two photos and try again.");
        setBusy(false);
        return;
      }
      if (editingId) {
        await api.patch(`/products/${editingId}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product is live");
      }
      setOpen(false); setEditingId(null); setForm(emptyProduct); reload();
    } catch (e) { toast.error(formatApiError(e, "Failed")); }
    finally { setBusy(false); }
  };

  const openNewProduct = () => {
    setEditingId(null);
    // Vendors are locked to their KYC business_category; admins can pick anything.
    const lockedCat = user?.role === "vendor" && profile?.kyc?.business_category
      ? profile.kyc.business_category
      : emptyProduct.category;
    const initial = suggestGstSlab(lockedCat, "", CATS);
    setForm({ ...emptyProduct, category: lockedCat, gst_slab: initial.slab });
    setGstOverridden(false);
    setOpen(true);
  };

  const openEditProduct = (p) => {
    const mrp = Number(p.original_price ?? p.price ?? 0);
    const price = Number(p.price ?? mrp);
    const discount = mrp > 0 && mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
    setEditingId(p.id);
    setForm({
      name: p.name || "",
      description: p.description || "",
      mrp: String(mrp || ""),
      discount: discount ? String(discount) : "",
      category: p.category || "womens-fashion",
      image_base64: p.image_base64 || "",
      images: Array.isArray(p.images) && p.images.length ? p.images : (p.image_base64 ? [p.image_base64] : []),
      stock: p.stock ?? 10,
      duration_hours: p.duration_hours ?? 48,
      size: p.size || "",
      sizes: Array.isArray(p.sizes) && p.sizes.length
        ? p.sizes
        : (p.size ? String(p.size).split(/[,/]/).map((s) => s.trim()).filter(Boolean) : []),
      color: p.color || "",
      material: p.material || "",
      specifications: p.specifications || "",
      gst_slab: typeof p.gst_slab === "number" ? p.gst_slab : 18,
      packaging_charge: typeof p.packaging_charge === "number" ? p.packaging_charge : 0,
      weight_kg: typeof p.weight_kg === "number" ? p.weight_kg : "",
      length_cm: typeof p.length_cm === "number" ? p.length_cm : "",
      breadth_cm: typeof p.breadth_cm === "number" ? p.breadth_cm : "",
      height_cm: typeof p.height_cm === "number" ? p.height_cm : "",
      hsn_code: p.hsn_code || "",
      delivery_within_city: p.delivery_within_city !== false,
      delivery_within_state: p.delivery_within_state !== false,
      delivery_out_of_state: p.delivery_out_of_state !== false,
      cod_available: p.cod_available !== false,
    });
    // Treat existing products as manually-set so we don't override the stored slab automatically.
    setGstOverridden(true);
    setOpen(true);
  };

  const handleDialogChange = (isOpen) => {
    setOpen(isOpen);
    if (!isOpen) {
      setEditingId(null);
      setForm(emptyProduct);
      setGstOverridden(false);
    }
  };

  const delProduct = async (id) => {
    if (!window.confirm("Remove this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Product removed"); reload();
  };

  const saveProfile = async () => {
    setBusy(true);
    try {
      await api.patch("/exhibitor/profile", profileForm);
      toast.success("Profile saved");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (!user) return <div className="p-10">Loading exhibitor backend…</div>;
  const analyticsSafe = analytics || {
    products: { live: 0, expired: 0, total: 0 },
    orders: { count: 0, units_sold: 0 },
    revenue: {
      gross: 0, commission_rate: 0,
      commission_config: { slab1_pct: 0, slab2_pct: 0 },
      commission_total: 0, net_earnings: 0, pending_payout: 0, paid_out: 0,
    },
    by_category: [],
  };
  const kyc = profile?.kyc || {};

  const stat = (Icon, label, val, tint = "#F48E2E", testid, goToTab) => (
    <div
      className={`p-5 rounded-2xl bg-white border border-gray-200 ${goToTab ? "cursor-pointer hover:border-[#F48E2E] transition-colors" : ""}`}
      data-testid={testid}
      onClick={goToTab ? () => setTab(goToTab) : undefined}
      role={goToTab ? "button" : undefined}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
        <div className="p-2 rounded-full" style={{ backgroundColor: tint + "22", color: tint }}><Icon className="h-4 w-4" /></div>
      </div>
      <div className="font-display text-3xl">{val}</div>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 lg:px-8 py-10" data-testid="vendor-dashboard">
      {profile?.account_status && profile.account_status !== "active" && (
        <div
          data-testid="vendor-status-banner"
          className={`mb-6 rounded-2xl border p-4 ${profile.account_status === "suspended" ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}
        >
          <div className="font-display text-lg mb-1">
            {profile.account_status === "suspended" ? "Account suspended" : "Account paused"}
          </div>
          <p className="text-sm leading-relaxed">
            {profile.account_status === "suspended"
              ? "All your listings are hidden from buyers and new uploads are disabled."
              : "New product uploads are temporarily disabled. Your existing listings remain visible to buyers."}
            {profile.account_status_reason && <> Reason: <b>{profile.account_status_reason}</b>.</>}
            {" "}Please contact CityShor support at <a href="mailto:Hi@cityshor.com" className="underline font-medium">Hi@cityshor.com</a>.
          </p>
        </div>
      )}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-2">Exhibitor Backend</div>
          <h1 className="font-display text-4xl inline-flex items-center gap-2">
            Hello, {user.full_name}
            {profile?._verified && <VerifiedBadge size="h-6 w-6" testid="vendor-header-verified" />}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-600">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FEF3E6] text-[#F48E2E] font-mono-tab font-bold text-xs">Vendor Code: {user.vendor_code || "pending"}</span>
            <span>Commission: <b>{analyticsSafe.revenue.commission_config.slab1_pct}–{analyticsSafe.revenue.commission_config.slab2_pct}%</b> <span className="text-gray-400">(by order value)</span></span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="vendor-notif-bell"
            onClick={() => setTab("notif")}
            className="relative p-2.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50"
            aria-label="Notifications"
          >
            <Bell className="h-5 w-5 text-gray-700" />
            {unreadNotifCount > 0 && (
              <span data-testid="vendor-notif-badge" className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                {unreadNotifCount > 9 ? "9+" : unreadNotifCount}
              </span>
            )}
          </button>
          <Dialog open={open} onOpenChange={handleDialogChange}>
          <DialogTrigger asChild>
            <Button data-testid="vendor-add-product" onClick={openNewProduct} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815]">
              <PlusCircle className="h-4 w-4 mr-2" /> Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingId ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Product Images <span className="text-gray-400 font-normal">(up to 6 — first is the cover)</span></Label>
                <div className="mt-1 space-y-2">
                  {(form.images && form.images.length > 0) && (
                    <div className="flex flex-wrap gap-2" data-testid="vendor-img-thumbs">
                      {form.images.map((src, idx) => (
                        <div key={idx} className="relative">
                          <img src={src} className="h-16 w-16 object-cover rounded-md border border-gray-200" alt="" />
                          {idx === 0 && <span className="absolute -top-1.5 -left-1.5 text-[9px] bg-[#F48E2E] text-white px-1.5 py-0.5 rounded-full">Cover</span>}
                          <button
                            type="button"
                            data-testid={`vendor-img-remove-${idx}`}
                            onClick={() => removeImage(idx)}
                            className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-500 text-white text-[11px] flex items-center justify-center hover:bg-red-600"
                            aria-label={`Remove image ${idx + 1}`}
                          >×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {(form.images?.length || 0) < 6 && (
                    <label className="cursor-pointer flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-xl hover:border-[#F48E2E]">
                      <Upload className="h-4 w-4" />
                      <span className="text-sm">Add image{(form.images?.length || 0) > 0 ? `s (${6 - form.images.length} left)` : "s (up to 6)"}</span>
                      <input type="file" accept="image/*" multiple hidden onChange={onFile} data-testid="vendor-img-input" />
                    </label>
                  )}
                  <p className="text-[11px] text-gray-500">Any size or shape works — photos are automatically fitted to the store’s 4:5 portrait frame (1200 × 1500). First image becomes the marketplace thumbnail; others show in the product gallery.</p>
                </div>
              </div>
              <div><Label>Product Name <span className="text-red-500">*</span></Label><Input data-testid="vendor-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>

              <div>
                <Label>Category <span className="text-red-500">*</span></Label>
                {user.role === "vendor" && profile?.kyc?.business_category ? (
                  (() => {
                    const vendorCat = profile.kyc.business_category;
                    const subs = cats.filter((c) => c.parent_slug === vendorCat);
                    const options = [{ slug: vendorCat, name: CATS.find((c) => c.slug === vendorCat)?.name || vendorCat }, ...subs];
                    return (
                      <>
                        <Select value={form.category} onValueChange={(v) => {
                          setGstOverridden(false);
                          setForm({ ...form, category: v });
                        }}>
                          <SelectTrigger data-testid="vendor-cat"><SelectValue /></SelectTrigger>
                          <SelectContent>{options.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <p className="text-[11px] text-gray-500 mt-1">Choose your registered category or one of its subcategories.</p>
                      </>
                    );
                  })()
                ) : (
                  <Select value={form.category} onValueChange={(v) => {
                    setGstOverridden(false);
                    setForm({ ...form, category: v });
                  }}>
                    <SelectTrigger data-testid="vendor-cat"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATS.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>

              <div><Label>Product Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>HSN Code <span className="text-gray-400 font-normal">(optional)</span></Label>
                  <Input data-testid="vendor-hsn" placeholder="e.g. 6204" inputMode="numeric" pattern="\d{4,8}" maxLength={8} value={form.hsn_code} onChange={(e) => setForm({ ...form, hsn_code: e.target.value.replace(/[^0-9]/g, "").slice(0, 8) })} />
                </div>
                <div>
                  <Label>Packaging charge (₹) <span className="text-gray-400 font-normal">per unit</span></Label>
                  <Input
                    data-testid="vendor-packaging-charge"
                    type="number" min="0" step="1"
                    placeholder="0"
                    value={form.packaging_charge}
                    onChange={(e) => setForm({ ...form, packaging_charge: Math.max(0, Number(e.target.value) || 0) })}
                  />
                  <div className="text-[11px] text-gray-500 mt-1">Added to the buyer's order as a pass-through — returned to you in full at payout.</div>
                  <p className="text-[11px] text-gray-500 mt-1">Required only if your business is GST registered. 4 to 8 digits.</p>
                </div>
                <div>
                  <Label>GST Rate <span className="text-red-500">*</span></Label>
                  <Select value={String(form.gst_slab)} onValueChange={(v) => { setGstOverridden(true); setForm({ ...form, gst_slab: Number(v) }); }}>
                    <SelectTrigger data-testid="vendor-gst-slab"><SelectValue placeholder="Select GST rate" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0% (exempt)</SelectItem>
                      <SelectItem value="5">5%</SelectItem>
                      <SelectItem value="12">12%</SelectItem>
                      <SelectItem value="18">18%</SelectItem>
                      <SelectItem value="28">28%</SelectItem>
                    </SelectContent>
                  </Select>
                  <p data-testid="vendor-gst-hint" className="mt-1 text-[11px] leading-snug text-gray-500">
                    {gstSuggestion.reason}
                    {gstOverridden && Number(form.gst_slab) !== gstSuggestion.slab && (
                      <>
                        {" "}
                        <button
                          type="button"
                          data-testid="vendor-gst-apply-suggestion"
                          className="underline text-[#F48E2E] hover:text-[#d97a1f]"
                          onClick={() => { setGstOverridden(false); setForm({ ...form, gst_slab: gstSuggestion.slab }); }}
                        >
                          Apply {gstSuggestion.slab}%
                        </button>
                      </>
                    )}
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-dashed border-gray-200 p-3">
                <div className="text-[11px] uppercase tracking-widest text-gray-500 font-bold mb-1">Shipping dimensions <span className="text-gray-400 font-normal normal-case tracking-normal">(optional — defaults to 0.5 kg · 20×15×10 cm)</span></div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div>
                    <Label className="text-[11px]">Weight (kg)</Label>
                    <Input data-testid="vendor-weight-kg" type="number" min="0" step="0.01" placeholder="0.5" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} className="h-9" />
                  </div>
                  <div>
                    <Label className="text-[11px]">Length (cm)</Label>
                    <Input data-testid="vendor-length-cm" type="number" min="0" step="0.5" placeholder="20" value={form.length_cm} onChange={(e) => setForm({ ...form, length_cm: e.target.value })} className="h-9" />
                  </div>
                  <div>
                    <Label className="text-[11px]">Breadth (cm)</Label>
                    <Input data-testid="vendor-breadth-cm" type="number" min="0" step="0.5" placeholder="15" value={form.breadth_cm} onChange={(e) => setForm({ ...form, breadth_cm: e.target.value })} className="h-9" />
                  </div>
                  <div>
                    <Label className="text-[11px]">Height (cm)</Label>
                    <Input data-testid="vendor-height-cm" type="number" min="0" step="0.5" placeholder="10" value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })} className="h-9" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>MRP (₹) <span className="text-gray-400 font-normal">excl. GST</span></Label>
                  <Input data-testid="vendor-mrp" type="number" min="0" placeholder="e.g. 500" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} />
                </div>
                <div>
                  <Label>Discount (%) <span className="text-gray-400 font-normal">optional</span></Label>
                  <Input data-testid="vendor-discount" type="number" min="0" max="100" placeholder="0 to 100" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} />
                </div>
                <div className="col-span-2 -mt-1">
                  <div className="rounded-xl bg-[#FEF3E6] border border-[#F48E2E]/30 px-3 py-2 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Selling price shown to buyers (excl. GST)</span>
                      <span className="font-mono-tab text-sm font-semibold text-[#111827]">
                        {form.mrp
                          ? `₹${(Math.round((Number(form.mrp) - (Number(form.mrp) * Number(form.discount || 0)) / 100) * 100) / 100).toLocaleString("en-IN")}`
                          : "Enter MRP above"}
                      </span>
                    </div>
                    {form.mrp && (
                      <div className="flex items-center justify-between text-[11px] text-gray-500">
                        <span>Buyer pays at checkout (with {form.gst_slab}% GST)</span>
                        <span className="font-mono-tab">
                          {(() => {
                            const sp = Math.round((Number(form.mrp) - (Number(form.mrp) * Number(form.discount || 0)) / 100) * 100) / 100;
                            const gross = Math.round(sp * (1 + Number(form.gst_slab) / 100) * 100) / 100;
                            return `₹${gross.toLocaleString("en-IN")}`;
                          })()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div><Label>Stock Quantity</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></div>
                <div>
                  <Label>Live for (hours, 48–240)</Label>
                  <Input data-testid="vendor-duration-hours" type="number" min="48" max="240" value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Available Sizes</Label>
                  <div className="mt-1 space-y-2" data-testid="vendor-sizes-block">
                    <div className="flex flex-wrap gap-1.5">
                      {PRESET_SIZES.map((s) => {
                        const active = (form.sizes || []).includes(s);
                        return (
                          <button
                            key={s}
                            type="button"
                            data-testid={`vendor-size-preset-${s}`}
                            onClick={() => setForm({
                              ...form,
                              sizes: active
                                ? form.sizes.filter((x) => x !== s)
                                : [...(form.sizes || []), s],
                            })}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-[#F48E2E] text-white border-[#F48E2E]" : "bg-white text-gray-600 border-gray-200 hover:border-[#F48E2E] hover:text-[#F48E2E]"}`}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        data-testid="vendor-size-custom-input"
                        placeholder="Custom size (e.g. 24 x 36 in)"
                        value={form._customSize || ""}
                        onChange={(e) => setForm({ ...form, _customSize: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const v = (form._customSize || "").trim();
                            if (v && !(form.sizes || []).includes(v)) {
                              setForm({ ...form, sizes: [...(form.sizes || []), v], _customSize: "" });
                            }
                          }
                        }}
                        className="h-9 text-sm"
                      />
                      <Button
                        type="button"
                        data-testid="vendor-size-custom-add"
                        onClick={() => {
                          const v = (form._customSize || "").trim();
                          if (v && !(form.sizes || []).includes(v)) {
                            setForm({ ...form, sizes: [...(form.sizes || []), v], _customSize: "" });
                          }
                        }}
                        variant="outline"
                        className="h-9 rounded-full text-xs whitespace-nowrap"
                      >Add</Button>
                    </div>
                    {(form.sizes || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1" data-testid="vendor-sizes-selected">
                        {(form.sizes || []).map((s) => (
                          <span key={s} data-testid={`vendor-size-chip-${s}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#FEF3E6] text-[#B65C1A] border border-[#F48E2E]/40">
                            {s}
                            <button
                              type="button"
                              onClick={() => setForm({ ...form, sizes: form.sizes.filter((x) => x !== s) })}
                              className="text-[#B65C1A] hover:text-red-600"
                              aria-label={`Remove ${s}`}
                            >×</button>
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-gray-500">Leave empty if this product has no size (e.g. gift items). Buyers will need to pick one at checkout when sizes are set.</p>
                  </div>
                </div>
                <div><Label>Colour</Label><Input data-testid="vendor-color" placeholder="Rani pink / Royal blue" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div>
                <div><Label>Material</Label><Input data-testid="vendor-material" placeholder="Cotton / Silk / Brass" value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} /></div>
                <div className="col-span-2"><Label>Specifications</Label><Textarea data-testid="vendor-specs" placeholder="Wash care, dimensions, handmade details, etc." value={form.specifications} onChange={(e) => setForm({ ...form, specifications: e.target.value })} /></div>
              </div>

              <div className="mt-2 p-4 rounded-xl bg-gray-50 border border-gray-200" data-testid="vendor-shipping-settings">
                <div className="text-xs uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-1">Shipping Settings</div>
                <p className="text-xs text-gray-500 mb-3">Where can you ship this product and how quickly? Buyers see these on the product page.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
                  <label className="flex items-start gap-2 p-3 rounded-lg border border-gray-200 bg-white cursor-pointer">
                    <Checkbox data-testid="delivery-city" checked={form.delivery_within_city} onCheckedChange={(v) => setForm({ ...form, delivery_within_city: !!v })} className="mt-0.5" />
                    <div>
                      <div className="text-sm font-medium">Within city</div>
                      <div className="text-[11px] text-gray-500">1 day</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 p-3 rounded-lg border border-gray-200 bg-white cursor-pointer">
                    <Checkbox data-testid="delivery-state" checked={form.delivery_within_state} onCheckedChange={(v) => setForm({ ...form, delivery_within_state: !!v })} className="mt-0.5" />
                    <div>
                      <div className="text-sm font-medium">Within state</div>
                      <div className="text-[11px] text-gray-500">2-3 days</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 p-3 rounded-lg border border-gray-200 bg-white cursor-pointer">
                    <Checkbox data-testid="delivery-outstate" checked={form.delivery_out_of_state} onCheckedChange={(v) => setForm({ ...form, delivery_out_of_state: !!v })} className="mt-0.5" />
                    <div>
                      <div className="text-sm font-medium">Out of state</div>
                      <div className="text-[11px] text-gray-500">3-5 days</div>
                    </div>
                  </label>
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-2">Payment Options</div>
                <label className="flex items-start gap-2 p-3 rounded-lg border border-gray-200 bg-white cursor-pointer">
                  <Checkbox data-testid="cod-available" checked={form.cod_available} onCheckedChange={(v) => setForm({ ...form, cod_available: !!v })} className="mt-0.5" />
                  <div>
                    <div className="text-sm font-medium">Cash on Delivery available</div>
                    <div className="text-[11px] text-gray-500">Uncheck to make this product Prepaid only (online payment).</div>
                  </div>
                </label>
              </div>

              <div className="mt-2 p-4 rounded-xl bg-gray-50 border border-gray-200" data-testid="vendor-product-offer">
                <div className="text-xs uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-1">Offer</div>
                {!editingId ? (
                  <p className="text-xs text-gray-500">Save this product first, then come back here to add a discount offer for it.</p>
                ) : (() => {
                  const productOffer = offers.find((o) => (o.product_ids || []).includes(editingId));
                  return productOffer ? (
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm">
                        <span className="font-mono-tab font-bold">{productOffer.code}</span> — {productOffer.discount_percent}% off
                        <span className={`ml-2 text-[11px] ${productOffer.active ? "text-green-600" : "text-gray-400"}`}>{productOffer.active ? "Active" : "Inactive"}</span>
                      </div>
                      <Button type="button" data-testid="vendor-product-offer-manage" variant="outline" className="rounded-full h-8 text-xs" onClick={() => { setOpen(false); openEditOffer(productOffer); }}>
                        Manage
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs text-gray-500">No offer set for this product yet.</p>
                      <Button type="button" data-testid="vendor-product-offer-create" variant="outline" className="rounded-full h-8 text-xs" onClick={() => { setOpen(false); openNewOffer([editingId]); }}>
                        Create offer
                      </Button>
                    </div>
                  );
                })()}
              </div>

              <Button data-testid="vendor-save" onClick={submitProduct} disabled={busy} className="w-full rounded-full bg-[#F48E2E] hover:bg-[#D97815]">
                {busy ? (editingId ? "Saving..." : "Publishing...") : (editingId ? "Save changes" : "Publish Product")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {user.role === "vendor" && kyc?.status && !(kyc.agreement_accepted && kyc.payout_policy_accepted) && (
        <div data-testid="vendor-agreements-banner" className="mb-6 p-4 rounded-xl border bg-amber-50 border-amber-200 text-amber-900">
          <div className="font-semibold mb-1">Vendor Agreement and Payout Policy not accepted</div>
          <p className="text-sm mb-3">Products can be uploaded only after your KYC is verified and both documents are accepted.</p>
          <label className="flex items-start gap-2 text-sm mb-2">
            <Checkbox data-testid="agree-vendor-agreement" checked={agreeForm.agreement} onCheckedChange={(v) => setAgreeForm({ ...agreeForm, agreement: !!v })} />
            <span>I have read and agree to the <a href="/legal/vendor-agreement" target="_blank" rel="noreferrer" className="text-[#F48E2E] underline">Vendor Agreement</a>.</span>
          </label>
          <label className="flex items-start gap-2 text-sm mb-3">
            <Checkbox data-testid="agree-payout-policy" checked={agreeForm.payout} onCheckedChange={(v) => setAgreeForm({ ...agreeForm, payout: !!v })} />
            <span>I have read and agree to the <a href="/legal/vendor-payout-policy" target="_blank" rel="noreferrer" className="text-[#F48E2E] underline">Vendor Payout Policy</a>.</span>
          </label>
          <Button
            data-testid="agree-submit"
            disabled={agreeBusy || !(agreeForm.agreement && agreeForm.payout)}
            onClick={async () => {
              setAgreeBusy(true);
              try {
                await api.post("/vendor/accept-agreements", { agreement_accepted: true, payout_policy_accepted: true });
                toast.success("Thank you. Both documents are recorded as accepted.");
                reload();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Could not record acceptance");
              } finally {
                setAgreeBusy(false);
              }
            }}
            className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white h-8 text-xs"
          >{agreeBusy ? "Saving…" : "Accept and continue"}</Button>
        </div>
      )}
      {user.role === "vendor" && profile && !kyc?.status && (
        <div data-testid="vendor-kyc-missing-banner" className="mb-6 p-4 rounded-xl border bg-amber-50 border-amber-200 text-amber-900 flex flex-wrap items-center justify-between gap-3">
          <div>Your KYC has not been submitted yet. Products cannot be published until KYC is verified.</div>
          <Button data-testid="vendor-complete-kyc" onClick={() => nav("/vendor-register")} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white h-8 text-xs">Complete KYC</Button>
        </div>
      )}
      {user.role === "vendor" && kyc?.status && (
        <div className={`mb-6 p-4 rounded-xl border ${kyc.status === "verified" ? "bg-green-50 border-green-200" : "bg-yellow-50 border-yellow-200"}`}>
          {kyc.status === "verified" ? (
            <div className="flex items-center gap-2 text-green-700"><CheckCircle2 className="h-4 w-4" /> Your exhibitor profile is verified. Buyers see the trusted badge on your products.</div>
          ) : (
            <div className="text-yellow-800">Verification status: <b>{kyc.status}</b>. Your products will go live once our team completes verification.</div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        {stat(Package, "Live products", analyticsSafe.products.live, "#F48E2E", "stat-live", "products")}
        {stat(Package, "Expired", analyticsSafe.products.expired, "#9CA3AF", "stat-expired", "products")}
        {stat(ShoppingBag, "Orders", analyticsSafe.orders.count, "#FF9800", "stat-orders", "orders")}
        {stat(TrendingUp, "Gross revenue", `₹${analyticsSafe.revenue.gross.toLocaleString("en-IN")}`, "#2196F3", "stat-gross", "payouts")}
        <div className="p-4 rounded-2xl bg-white border border-gray-200" data-testid="stat-wallet">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wider text-gray-500">Wallet balance</div>
            <div className="p-2 rounded-full" style={{ backgroundColor: "#F48E2E22", color: "#F48E2E" }}><Wallet className="h-4 w-4" /></div>
          </div>
          <div className="font-display text-3xl">₹{Number(wallet.balance || 0).toLocaleString("en-IN")}</div>
          <div className="mt-1 flex items-center gap-3">
            <WalletRechargeDialog balance={wallet.balance} onCredited={reload} />
            <button type="button" data-testid="wallet-boost-hint" onClick={() => setTab("products")} className="text-xs text-[#F48E2E] hover:underline">
              Use it to boost a product →
            </button>
          </div>
        </div>
      </div>

      <div className="mb-6 p-4 rounded-2xl bg-white border border-gray-200 flex items-start gap-3" data-testid="vendor-payments-note">
        <CheckCircle2 className="h-5 w-5 text-[#F48E2E] mt-0.5 flex-shrink-0" />
        <div className="text-sm text-gray-700">
          <span className="font-semibold text-gray-900">How payments work.</span> All order payments (COD, Online and Bank Transfer) are received into the Cityshor LLP account. Exhibitors are settled every week after delivery is confirmed, minus the platform commission ({analyticsSafe.revenue.commission_config.slab1_pct}–{analyticsSafe.revenue.commission_config.slab2_pct}% depending on order value).
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-gray-100">
          <TabsTrigger data-testid="tab-overview" value="overview">Overview</TabsTrigger>
          <TabsTrigger data-testid="tab-products" value="products">My Products</TabsTrigger>
          <TabsTrigger data-testid="tab-orders" value="orders">Orders</TabsTrigger>
          <TabsTrigger data-testid="tab-reviews" value="reviews">Reviews</TabsTrigger>
          <TabsTrigger data-testid="tab-returns" value="returns">Returns</TabsTrigger>
          <TabsTrigger data-testid="tab-offers" value="offers">Offers &amp; Delivery</TabsTrigger>
          <TabsTrigger data-testid="tab-live" value="live">Live</TabsTrigger>
          <TabsTrigger data-testid="tab-payouts" value="payouts">Payouts</TabsTrigger>
          <TabsTrigger data-testid="tab-profile" value="profile">Profile</TabsTrigger>
          <TabsTrigger data-testid="tab-notif" value="notif">Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="live" className="mt-6">
          <VendorLiveTab onChanged={reload} />
        </TabsContent>

        <TabsContent value="overview" className="mt-6 grid md:grid-cols-2 gap-4">
          <div className="p-6 bg-white rounded-2xl border border-gray-200">
            <div className="font-display text-xl mb-4">Revenue by Category</div>
            {analyticsSafe.by_category.length === 0 ? (
              <div className="text-sm text-gray-500 py-10 text-center">No sales yet. Publish curated products daily to start earning.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={analyticsSafe.by_category}>
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="revenue" fill="#F48E2E" radius={[8,8,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="p-6 bg-white rounded-2xl border border-gray-200">
            <div className="font-display text-xl mb-4">Earnings Snapshot</div>
            <div className="space-y-3">
              <div className="flex justify-between text-sm"><span>Gross revenue</span><span className="font-mono-tab">₹{analyticsSafe.revenue.gross.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between text-sm text-gray-500"><span>Platform commission ({analyticsSafe.revenue.gross > 0 ? Math.round((analyticsSafe.revenue.commission_total / analyticsSafe.revenue.gross) * 100) : 0}% effective)</span><span className="font-mono-tab">− ₹{(analyticsSafe.revenue.gross - analyticsSafe.revenue.net_earnings).toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between font-display text-lg pt-3 border-t"><span>Net earnings</span><span>₹{analyticsSafe.revenue.net_earnings.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between text-sm text-green-700"><span>Pending payout</span><span className="font-mono-tab">₹{analyticsSafe.revenue.pending_payout.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between text-sm text-gray-500"><span>Already paid out</span><span className="font-mono-tab">₹{analyticsSafe.revenue.paid_out.toLocaleString("en-IN")}</span></div>
            </div>
            <div className="mt-5 p-3 rounded-xl bg-[#FEF3E6] text-xs text-gray-700">
              Payouts are settled to your bank account (as per KYC) after order delivery is confirmed.
            </div>
          </div>
        </TabsContent>

        <TabsContent value="products" className="mt-6">
          {(() => {
            const live = products.filter((p) => !p.expired);
            const expiredList = products.filter((p) => p.expired);
            const resetTime = renewals.resets_at ? new Date(renewals.resets_at) : null;
            return (
              <div className="space-y-8">
                {expiredList.length > 0 && (
                  <div data-testid="expired-section">
                    <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
                      <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-gray-500 font-bold mb-1">Renew expired products</div>
                        <h3 className="font-display text-2xl">Bring your drops back live</h3>
                        <p className="text-xs text-gray-500 mt-1">One click restores photos, price and details for another {products[0]?.duration_hours || 24}-hour window. No re-uploading needed.</p>
                      </div>
                      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-2 text-right" data-testid="renewals-status">
                        <div className="text-[10px] uppercase tracking-widest text-gray-500 flex items-center justify-end gap-1"><Clock className="h-3 w-3" /> Renewals remaining today</div>
                        <div className="font-display text-xl text-[#F48E2E]">{renewals.remaining} <span className="text-gray-400 text-sm">/ {renewals.limit_per_day}</span></div>
                        {resetTime && <div className="text-[10px] text-gray-500">Resets {resetTime.toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}</div>}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {expiredList.map((p) => (
                        <div key={p.id} data-testid={`expired-${p.id}`} className="bg-white border border-gray-200 rounded-2xl overflow-hidden opacity-90">
                          <div className="relative aspect-square bg-gray-100">
                            <img src={p.image_base64} alt={p.name} className="w-full h-full object-cover grayscale" />
                            <span className="absolute top-2 left-2 bg-gray-900/80 text-white text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full">Expired</span>
                          </div>
                          <div className="p-3">
                            <div className="text-sm font-medium truncate">{p.name}</div>
                            <div className="text-xs text-gray-500">₹{p.price.toLocaleString("en-IN")} · {p.duration_hours}h window</div>
                            <Button
                              data-testid={`renew-${p.id}`}
                              onClick={() => renewProduct(p.id)}
                              disabled={renewals.remaining <= 0}
                              className="w-full mt-2 rounded-full h-9 bg-[#F48E2E] hover:bg-[#D97815] text-white text-xs disabled:opacity-50"
                            >
                              <RotateCw className="h-3 w-3 mr-1" /> {renewals.remaining <= 0 ? "Limit reached" : "Renew"}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <div className="font-display text-2xl mb-3">Live products</div>
                  {live.length === 0 ? (
                    <div className="text-sm text-gray-500 py-16 text-center border border-dashed border-gray-200 rounded-2xl">You have no products live right now. Renew an expired drop or click "Add Product" to publish a new one.</div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {live.map((p) => (
                        <div key={p.id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                          <div className="aspect-square bg-gray-100"><img src={p.image_base64} alt={p.name} className="w-full h-full object-cover" /></div>
                          <div className="p-3">
                            <div className="text-sm font-medium truncate">{p.name}</div>
                            <div className="text-sm text-gray-500">₹{p.price.toLocaleString("en-IN")}</div>
                            <div className="text-[10px] uppercase tracking-wider mt-1">
                              {p.visible === false ? (
                                <span className="text-amber-600">Awaiting KYC approval · not shown to buyers yet</span>
                              ) : (
                                <span className="text-gray-400">Live · {p.duration_hours}h</span>
                              )}
                            </div>
                            <div className="mt-2 flex items-center gap-3 flex-wrap">
                              <button data-testid={`vendor-edit-${p.id}`} onClick={() => openEditProduct(p)} className="text-xs text-[#F48E2E] hover:text-[#D97815] flex items-center gap-1 font-medium">
                                <Pencil className="h-3 w-3" /> Edit
                              </button>
                              <button data-testid={`vendor-del-${p.id}`} onClick={() => delProduct(p.id)} className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1">
                                <Trash2 className="h-3 w-3" /> Remove
                              </button>
                              {activeBoostByProduct[p.id] ? (
                                <div className="flex flex-col gap-1 w-full">
                                  <span data-testid={`vendor-boosted-${p.id}`} className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#FEF3E6] text-[#F48E2E] font-bold w-fit">
                                    Boosted · ends {new Date(activeBoostByProduct[p.id].end_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                                  </span>
                                  <label className="flex items-center gap-1.5 text-[11px] text-gray-600 cursor-pointer">
                                    <input
                                      data-testid={`vendor-boost-renew-${p.id}`}
                                      type="checkbox"
                                      checked={!!activeBoostByProduct[p.id].auto_renew}
                                      onChange={async (e) => {
                                        try {
                                          await api.patch(`/exhibitor/boosts/${activeBoostByProduct[p.id].id}/auto-renew`, { auto_renew: e.target.checked });
                                          toast.success(e.target.checked ? "Auto renew on — we'll keep this Sponsored slot alive." : "Auto renew off.");
                                          reload();
                                        } catch (err) { toast.error(err.response?.data?.detail || "Could not update"); }
                                      }}
                                      className="accent-[#F48E2E]"
                                    />
                                    Auto renew
                                  </label>
                                </div>
                              ) : (
                                <BoostProductDialog product={p} walletBalance={wallet.balance} onCreated={reload} />
                              )}
                              <SubmitToDropButton product={p} vendorId={user.id} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <ShiprocketPickupCard />
          <VendorCodCard />
          {orders.length === 0 ? (
            <div className="text-sm text-gray-500 py-16 text-center">No orders yet. Your first sale will appear here.</div>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <VendorOrderRow key={o.id} order={o} onReload={reload} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="reviews" className="mt-6">
          <VendorReviewsTab />
        </TabsContent>

        <TabsContent value="returns" className="mt-6">
          <VendorReturnsTab />
        </TabsContent>

        <TabsContent value="offers" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-display text-xl">Offers &amp; Free Delivery</div>
              <div className="text-xs text-gray-500 mt-1">Create discount codes 5%–50% or free delivery on the products you own. Buyers apply the code at checkout.</div>
            </div>
            <Dialog open={offerOpen} onOpenChange={(v) => { setOfferOpen(v); if (!v) { setEditingOfferId(null); setOfferForm(emptyOffer); } }}>
              <DialogTrigger asChild>
                <Button data-testid="vendor-new-offer" onClick={() => openNewOffer()} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815]">
                  <PlusCircle className="h-4 w-4 mr-2" /> New Offer
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{editingOfferId ? "Edit offer" : "New offer"}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Discount Code</Label>
                    <Input data-testid="offer-code" value={offerForm.code} onChange={(e) => setOfferForm({ ...offerForm, code: e.target.value.replace(/\s+/g, "").toUpperCase() })} placeholder="e.g. WELCOME10" disabled={!!editingOfferId} />
                    {editingOfferId && <p className="text-[11px] text-gray-500 mt-1">Code cannot be changed once created.</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Discount % (5 to 50)</Label>
                      <Input data-testid="offer-discount" type="number" min="5" max="50" value={offerForm.discount_percent} onChange={(e) => setOfferForm({ ...offerForm, discount_percent: Number(e.target.value) })} />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <Checkbox data-testid="offer-free-delivery" checked={offerForm.free_delivery} onCheckedChange={(v) => setOfferForm({ ...offerForm, free_delivery: !!v })} />
                        <Truck className="h-4 w-4 text-[#F48E2E]" /> Free delivery
                      </label>
                    </div>
                    <div>
                      <Label>Start Date</Label>
                      <Input data-testid="offer-start" type="date" value={offerForm.starts_at} onChange={(e) => setOfferForm({ ...offerForm, starts_at: e.target.value })} />
                    </div>
                    <div>
                      <Label>End Date</Label>
                      <Input data-testid="offer-end" type="date" value={offerForm.ends_at} onChange={(e) => setOfferForm({ ...offerForm, ends_at: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <Label>Apply to products</Label>
                    <div className="mt-2 max-h-60 overflow-y-auto border border-gray-200 rounded-xl p-2 space-y-1">
                      {products.length === 0 && <div className="text-xs text-gray-500 p-3 text-center">Publish a product first, then create an offer on it.</div>}
                      {products.map((p) => (
                        <label key={p.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                          <Checkbox data-testid={`offer-prod-${p.id}`} checked={offerForm.product_ids.includes(p.id)} onCheckedChange={() => toggleOfferProduct(p.id)} />
                          <img src={p.image_base64} alt="" className="h-8 w-8 rounded object-cover" />
                          <div className="flex-1 truncate text-sm">{p.name}</div>
                          <span className="text-xs text-gray-500 font-mono-tab">₹{p.price.toLocaleString("en-IN")}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1">Selected: <b>{offerForm.product_ids.length}</b></p>
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox data-testid="offer-active" checked={offerForm.active} onCheckedChange={(v) => setOfferForm({ ...offerForm, active: !!v })} />
                    Offer is active
                  </label>
                  <Button data-testid="offer-save" onClick={saveOffer} disabled={busy} className="w-full rounded-full bg-[#F48E2E] hover:bg-[#D97815]">
                    {busy ? "Saving..." : editingOfferId ? "Save changes" : "Create Offer"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {offers.length === 0 ? (
            <div className="text-sm text-gray-500 py-16 text-center border border-dashed border-gray-200 rounded-2xl">
              <Tag className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              You have not created any offers yet. Click "New Offer" to give shoppers a reason to check out today.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {offers.map((o) => {
                const now = new Date();
                const s = new Date(o.starts_at);
                const e = new Date(o.ends_at);
                const state = !o.active ? "off" : now < s ? "scheduled" : now > e ? "expired" : "live";
                const stateStyles = {
                  live: "bg-green-50 text-green-700 border-green-200",
                  scheduled: "bg-blue-50 text-blue-700 border-blue-200",
                  expired: "bg-gray-50 text-gray-500 border-gray-200",
                  off: "bg-yellow-50 text-yellow-700 border-yellow-200",
                }[state];
                return (
                  <div key={o.id} data-testid={`offer-card-${o.id}`} className="p-5 bg-white rounded-2xl border border-gray-200">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono-tab text-lg font-bold text-[#111827]">{o.code}</span>
                          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${stateStyles}`}>{state}</span>
                        </div>
                        <div className="text-sm text-gray-700">
                          <b>{o.discount_percent}% off</b>{o.free_delivery ? " · Free delivery" : ""} · {o.product_ids?.length || 0} product{o.product_ids?.length === 1 ? "" : "s"}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {s.toLocaleDateString("en-IN")} – {e.toLocaleDateString("en-IN")}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <button data-testid={`offer-toggle-${o.id}`} onClick={() => toggleOfferActive(o)} className={`px-2 py-1 rounded-full border ${o.active ? "border-yellow-300 text-yellow-800 hover:bg-yellow-50" : "border-green-300 text-green-700 hover:bg-green-50"}`}>
                          {o.active ? "Turn off" : "Turn on"}
                        </button>
                        <button data-testid={`offer-edit-${o.id}`} onClick={() => openEditOffer(o)} className="p-1 rounded-full text-[#F48E2E] hover:bg-orange-50"><Pencil className="h-3.5 w-3.5" /></button>
                        <button data-testid={`offer-delete-${o.id}`} onClick={() => deleteOffer(o.id)} className="p-1 rounded-full text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>


        <TabsContent value="payouts" className="mt-6">
          <VendorPayouts kyc={kyc} />
        </TabsContent>

        <TabsContent value="profile" className="mt-6">
          <div className="p-6 bg-white rounded-2xl border border-gray-200 max-w-2xl space-y-4">
            <div className="font-display text-xl">Public Profile & Contact</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input data-testid="prof-phone" value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} /></div>
              <div><Label>WhatsApp</Label><Input data-testid="prof-whatsapp" value={profileForm.whatsapp} onChange={(e) => setProfileForm({ ...profileForm, whatsapp: e.target.value })} /></div>
              <div>
                <Label>Instagram</Label>
                <div className="flex items-center border border-gray-200 rounded-md">
                  <span className="px-3 text-sm text-gray-500 border-r border-gray-200"><Instagram className="h-3.5 w-3.5" /></span>
                  <input data-testid="prof-instagram" value={profileForm.instagram_handle} onChange={(e) => setProfileForm({ ...profileForm, instagram_handle: e.target.value })} placeholder="yourbrand" className="flex-1 px-3 py-2 text-sm bg-transparent focus:outline-none" />
                </div>
              </div>
              <div>
                <Label>Facebook</Label>
                <div className="flex items-center border border-gray-200 rounded-md">
                  <span className="px-3 text-sm text-gray-500 border-r border-gray-200"><Facebook className="h-3.5 w-3.5" /></span>
                  <input data-testid="prof-facebook" value={profileForm.facebook_handle} onChange={(e) => setProfileForm({ ...profileForm, facebook_handle: e.target.value })} placeholder="yourbrand" className="flex-1 px-3 py-2 text-sm bg-transparent focus:outline-none" />
                </div>
              </div>
              <div className="col-span-2">
                <Label>Website</Label>
                <div className="flex items-center border border-gray-200 rounded-md">
                  <span className="px-3 text-sm text-gray-500 border-r border-gray-200"><Globe className="h-3.5 w-3.5" /></span>
                  <input data-testid="prof-website" value={profileForm.website_url} onChange={(e) => setProfileForm({ ...profileForm, website_url: e.target.value })} placeholder="https://" className="flex-1 px-3 py-2 text-sm bg-transparent focus:outline-none" />
                </div>
              </div>
              <div className="col-span-2"><Label>Business Address</Label><Textarea data-testid="prof-address" value={profileForm.business_address} onChange={(e) => setProfileForm({ ...profileForm, business_address: e.target.value })} /></div>
            </div>
            <Button data-testid="prof-save" onClick={saveProfile} disabled={busy} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815]">Save changes</Button>
          </div>
        </TabsContent>

        <TabsContent value="notif" className="mt-6">
          {notifs.length === 0 ? (
            <div className="text-sm text-gray-500 py-16 text-center">No notifications yet.</div>
          ) : (
            <div className="space-y-3">
              {notifs.map((n) => (
                <div key={n.id} className="p-4 bg-white border border-gray-200 rounded-2xl">
                  <div className="flex items-start gap-3">
                    <Bell className="h-4 w-4 text-[#F48E2E] mt-1" />
                    <div>
                      <div className="font-medium">{n.title}</div>
                      <div className="text-sm text-gray-600 mt-1">{n.body}</div>
                      <div className="text-xs text-gray-400 mt-2">{new Date(n.created_at).toLocaleString("en-IN")}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- Per-vendor COD switch (buyers must prepay when off) ----------
function VendorCodCard() {
  const [enabled, setEnabled] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/exhibitor/profile").then((r) => setEnabled(r.data?.kyc?.cod_enabled !== false)).catch(() => {}).finally(() => setLoaded(true));
  }, []);
  const toggle = async (v) => {
    setBusy(true);
    try {
      await api.put("/exhibitor/cod-settings", { cod_enabled: v });
      setEnabled(v);
      toast.success(v ? "COD enabled for your products" : "COD disabled — buyers must prepay for your products");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update COD setting");
    } finally { setBusy(false); }
  };
  if (!loaded) return null;
  return (
    <div data-testid="vendor-cod-card" className={`mb-4 p-4 rounded-2xl border ${enabled ? "bg-white border-gray-200" : "bg-orange-50 border-orange-200"}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold">Cash on Delivery (COD)</div>
          <div className="text-xs text-gray-600">{enabled ? "Buyers can pay cash on delivery for your products. A ₹100 COD handling charge applies to the buyer." : "COD is off — buyers must pay online or via bank transfer for your products."}</div>
        </div>
        <Switch data-testid="vendor-cod-toggle" checked={enabled} disabled={busy} onCheckedChange={toggle} />
      </div>
    </div>
  );
}

// ---------- Shiprocket pickup location (needed for auto-shipping + live delivery rates) ----------
function ShiprocketPickupCard() {
  const [current, setCurrent] = useState({ shiprocket_pickup_nickname: "", shiprocket_pickup_pincode: "" });
  const [locations, setLocations] = useState([]);
  const [locError, setLocError] = useState("");
  const [name, setName] = useState("");
  const [pincode, setPincode] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/exhibitor/shiprocket-pickup").then((r) => { setCurrent(r.data); setName(r.data.shiprocket_pickup_nickname || ""); setPincode(r.data.shiprocket_pickup_pincode || ""); }).catch(() => {});
    api.get("/shiprocket/pickup-locations").then((r) => { setLocations(r.data.locations || []); setLocError(r.data.error || ""); }).catch(() => setLocError("Could not load Shiprocket pickup locations."));
  }, []);
  const save = async () => {
    if (!name.trim()) return toast.error("Choose or enter your pickup location");
    setSaving(true);
    try {
      const { data } = await api.post("/exhibitor/shiprocket-pickup", { shiprocket_pickup_nickname: name.trim(), pincode: pincode.trim() || undefined });
      setCurrent(data);
      toast.success("Pickup location saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save pickup location");
    } finally {
      setSaving(false);
    }
  };
  const isSet = !!current.shiprocket_pickup_nickname;
  return (
    <div data-testid="shiprocket-pickup-card" className={`mb-4 p-4 rounded-2xl border ${isSet ? "bg-white border-gray-200" : "bg-orange-50 border-orange-200"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold">Shiprocket pickup location</div>
          <div className="text-xs text-gray-600">{isSet ? `Orders you confirm ship from "${current.shiprocket_pickup_nickname}"${current.shiprocket_pickup_pincode ? ` (${current.shiprocket_pickup_pincode})` : ""}.` : "Set this once so confirmed orders are shipped automatically via Shiprocket."}</div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
        {locations.length > 0 ? (
          <Select value={name} onValueChange={(v) => { setName(v); const l = locations.find((x) => x.name === v); if (l?.pin_code) setPincode(l.pin_code); }}>
            <SelectTrigger data-testid="pickup-select" className="h-9 text-sm"><SelectValue placeholder="Choose pickup location" /></SelectTrigger>
            <SelectContent>
              {locations.map((l) => <SelectItem key={l.name} value={l.name}>{l.name}{l.city ? ` · ${l.city}` : ""}{l.pin_code ? ` · ${l.pin_code}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <Input data-testid="pickup-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Pickup location name (as in Shiprocket)" className="h-9 text-sm" />
        )}
        <Input data-testid="pickup-pincode" value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Pickup pincode" className="h-9 text-sm" />
        <Button data-testid="pickup-save" onClick={save} disabled={saving} className="h-9 rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white text-xs">{saving ? "Saving…" : "Save pickup location"}</Button>
      </div>
      {locError && <div className="mt-2 text-[11px] text-gray-500">{locError}</div>}
    </div>
  );
}

// ---------- Vendor order row with status, tracking & timeline ----------
const STATUS_STEPS = ["awaiting_vendor", "processing", "packed", "shipped", "delivered"];
const STATUS_LABEL = { awaiting_payment: "Awaiting payment", awaiting_vendor: "Awaiting your confirmation", processing: "Processing", confirmed: "Confirmed", packed: "Packed", shipped: "Shipped", delivered: "Delivered", cancelled: "Cancelled", payment_failed: "Payment failed" };
const STATUS_TONE = {
  awaiting_payment: "bg-gray-50 text-gray-600 border-gray-200",
  awaiting_vendor: "bg-orange-50 text-orange-700 border-orange-200",
  processing: "bg-blue-50 text-blue-700 border-blue-200",
  payment_failed: "bg-red-50 text-red-700 border-red-200",
  confirmed: "bg-blue-50 text-blue-700 border-blue-200",
  packed: "bg-purple-50 text-purple-700 border-purple-200",
  shipped: "bg-amber-50 text-amber-700 border-amber-200",
  delivered: "bg-green-50 text-green-700 border-green-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

function VendorOrderRow({ order: o, onReload }) {
  const { user } = useAuth();
  const [tracking, setTracking] = useState(o.tracking_number || "");
  const [courier, setCourier] = useState(o.courier || "");
  const [busy, setBusy] = useState(false);
  const [savingTracking, setSavingTracking] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const needsVendorConfirm = ["awaiting_vendor", "confirmed"].includes(o.status) && !o.vendor_confirmed_at;

  const confirmOrder = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post(`/exhibitor/orders/${o.id}/confirm`);
      toast.success("Order confirmed. The customer and CityShor have been notified.");
      if (data?.shipment?.created) toast.success(`Shiprocket shipment created${data.shipment.awb ? ` · AWB ${data.shipment.awb}` : ""}`);
      else if (data?.shipment?.error) toast.error(data.shipment.error);
      if (data?.shipment?.pickup && !data.shipment.pickup.ok) toast.error(`Pickup request failed: ${data.shipment.pickup.error}. Use Request pickup on the order card.`);
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm the order");
    } finally {
      setConfirming(false);
    }
  };

  const retryShiprocket = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post(`/exhibitor/orders/${o.id}/ship-via-shiprocket`, {});
      toast.success(`Shiprocket shipment ready${data?.shipment?.awb_code ? ` · AWB ${data.shipment.awb_code}` : ""}`);
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Shiprocket retry failed");
    } finally {
      setRetrying(false);
    }
  };

  const requestPickup = async () => {
    setRetrying(true);
    try {
      await api.post(`/exhibitor/orders/${o.id}/shipment/pickup`, { pickup_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10) });
      toast.success("Pickup requested with Shiprocket");
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pickup request failed");
    } finally {
      setRetrying(false);
    }
  };

  const dispatchStatus = async (nextStatus) => {
    if (nextStatus === o.status) return;
    if (nextStatus === "shipped" && !tracking.trim() && !(o.tracking_number || "").trim()) {
      toast.error("Enter a tracking number below before marking this order shipped.");
      return;
    }
    if (nextStatus === "cancelled" && !window.confirm(`Cancel order ${o.id.slice(0, 8).toUpperCase()}? Stock will be returned to inventory.`)) {
      return;
    }
    setBusy(true);
    try {
      await api.post(`/exhibitor/orders/${o.id}/status`, {
        status: nextStatus,
        tracking_number: tracking.trim() || undefined,
        courier: courier.trim() || undefined,
      });
      toast.success(`Marked ${STATUS_LABEL[nextStatus]}`);
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    } finally {
      setBusy(false);
    }
  };

  const saveTracking = async () => {
    if (!tracking.trim()) return toast.error("Please enter a tracking number");
    setSavingTracking(true);
    try {
      await api.post(`/exhibitor/orders/${o.id}/status`, {
        status: o.status || "shipped",
        tracking_number: tracking.trim(),
        courier: courier.trim() || undefined,
      });
      toast.success("Tracking saved");
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save tracking");
    } finally {
      setSavingTracking(false);
    }
  };

  const isCancelled = o.status === "cancelled";
  const currentIdx = STATUS_STEPS.indexOf(o.status === "confirmed" ? "awaiting_vendor" : o.status);
  const showTracking = ["shipped", "delivered"].includes(o.status) || !!o.tracking_number;

  return (
    <div data-testid={`exh-order-${o.id}`} className="p-4 bg-white border border-gray-200 rounded-2xl">
      <div className="flex flex-wrap justify-between gap-3 items-start">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-xs font-mono-tab text-gray-500">#{o.id.slice(0, 8).toUpperCase()}</div>
            <span data-testid={`order-badge-${o.id}`} className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 rounded-full ${STATUS_TONE[o.status] || "bg-gray-50 text-gray-600 border-gray-200"}`}>{STATUS_LABEL[o.status] || o.status}</span>
          </div>
          <div className="text-xs text-gray-500 mt-0.5">{new Date(o.created_at).toLocaleString("en-IN")}</div>
          <div className="mt-2 text-sm">
            {o.items.map((i) => (
              <div key={i.product_id} className="flex items-center gap-2">
                <img src={i.image_base64} className="h-8 w-8 rounded object-cover" alt="" />
                <span className="truncate max-w-[220px]">{i.name}</span>
                <span className="text-gray-500">×{i.quantity}</span>
              </div>
            ))}
          </div>
          <OrderStagePill order={o} />
          {isCancelled && o.refund_status && o.refund_status !== "not_required" && (
            <div data-testid={`order-refund-status-${o.id}`} className={`mt-2 inline-block text-[11px] px-2 py-1 rounded-full border ${o.refund_status === "processed" ? "bg-green-50 text-green-700 border-green-200" : o.refund_status === "failed" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
              {o.refund_status === "processed"
                ? `Refund issued · ₹${Number(o.refund_amount || 0).toLocaleString("en-IN")}${o.refund_method ? ` via ${{ razorpay: "Razorpay", cash: "cash", bank_transfer: "bank transfer", upi: "UPI" }[o.refund_method] || o.refund_method}` : ""}`
                : o.refund_status === "failed"
                  ? "Refund failed — CityShor support will resolve it"
                  : "Refund pending — CityShor is processing it"}
            </div>
          )}
          <div className="text-xs text-gray-500 mt-2">Ship to: {o.address?.full_name}, {o.address?.city}, {o.address?.pincode}. {o.payment_method === "cod" ? "COD" : "Prepaid"}</div>
          {(() => {
            const sr = (o.shipments || []).find((s) => s.vendor_id === user?.id && s.shipment_id) || (o.shipments || []).find((s) => s.shipment_id);
            if (sr?.awb_code) {
              return (
                <div data-testid={`order-shiprocket-awb-${o.id}`} className="mt-2 text-xs text-green-700">
                  Shiprocket AWB: <span className="font-mono-tab">{sr.awb_code}</span>{sr.courier_name ? ` · ${sr.courier_name}` : ""}
                  {sr.label_url && <> · <a href={sr.label_url} target="_blank" rel="noreferrer" className="underline">Label</a></>}
                  <div data-testid={`order-pickup-status-${o.id}`} className={sr.pickup_requested_at ? "text-green-700" : "text-amber-700"}>
                    {sr.pickup_requested_at ? `Pickup requested${sr.pickup_scheduled_date ? ` for ${sr.pickup_scheduled_date}` : ""}${sr.pickup_token ? ` · token ${sr.pickup_token}` : ""}` : (sr.pickup_error || "Pickup not requested yet")}
                    {!sr.pickup_requested_at && <Button data-testid={`order-request-pickup-${o.id}`} onClick={requestPickup} disabled={retrying} className="ml-2 h-6 rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white text-[10px] px-2">Request pickup</Button>}
                  </div>
                </div>
              );
            }
            if (sr && !sr.awb_code && !isCancelled) {
              return (
                <div data-testid={`order-awb-pending-${o.id}`} className="mt-2 p-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex flex-wrap items-center justify-between gap-2">
                  <span>Shipment created but no AWB yet: {sr.awb_error || o.shiprocket_error || "Shiprocket has not assigned a courier."}</span>
                  <Button data-testid={`order-retry-awb-${o.id}`} onClick={retryShiprocket} disabled={retrying} className="h-7 rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white text-[11px] px-3">{retrying ? "Retrying…" : "Retry AWB + pickup"}</Button>
                </div>
              );
            }
            const notSent = !sr && ["processing", "packed"].includes(o.status);
            if ((o.shiprocket_error || notSent) && !isCancelled) {
              return (
                <div data-testid={`order-shiprocket-error-${o.id}`} className="mt-2 p-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex flex-wrap items-center justify-between gap-2">
                  <span>Shipment not created: {o.shiprocket_error || "this order has not reached Shiprocket yet."}</span>
                  <Button data-testid={`order-retry-shiprocket-${o.id}`} onClick={retryShiprocket} disabled={retrying} className="h-7 rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white text-[11px] px-3">{retrying ? "Retrying…" : "Retry Shiprocket shipment"}</Button>
                </div>
              );
            }
            return null;
          })()}
        </div>
        <div className="text-right">
          <div className="font-display text-lg">₹{o.my_subtotal.toLocaleString("en-IN")}</div>
          <div className="text-xs text-green-700">Your earning: ₹{o.my_net_earning.toLocaleString("en-IN")}</div>
          {needsVendorConfirm && (
            <Button data-testid={`order-confirm-${o.id}`} onClick={confirmOrder} disabled={confirming} className="mt-2 w-40 h-8 rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white text-xs block ml-auto">
              {confirming ? "Confirming…" : "Confirm order"}
            </Button>
          )}
          <Select value={o.status} onValueChange={dispatchStatus} disabled={busy || needsVendorConfirm || o.status === "awaiting_payment"}>
            <SelectTrigger data-testid={`order-status-${o.id}`} className="mt-2 w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(STATUS_LABEL[o.status] && !["processing", "packed", "shipped", "delivered", "cancelled"].includes(o.status) ? [o.status] : []).concat(["processing", "packed", "shipped", "delivered", "cancelled"]).map((s) => (
                <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!isCancelled && (
        <div className="mt-4 flex items-center gap-2" data-testid={`order-timeline-${o.id}`}>
          {STATUS_STEPS.map((s, idx) => {
            const done = currentIdx >= idx;
            return (
              <div key={s} className="flex-1 flex items-center gap-2">
                <div className={`h-2 w-2 rounded-full ${done ? "bg-[#F48E2E]" : "bg-gray-200"}`} />
                <div className={`flex-1 h-px ${done && idx < STATUS_STEPS.length - 1 ? "bg-[#F48E2E]" : "bg-gray-200"}`} />
                <span className={`text-[10px] uppercase tracking-widest ${done ? "text-[#F48E2E]" : "text-gray-400"}`}>{STATUS_LABEL[s]}</span>
              </div>
            );
          })}
        </div>
      )}

      {(showTracking || o.status === "packed") && !isCancelled && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2">
          <div>
            <Label className="text-[11px]">Tracking Number {o.status === "shipped" && !o.tracking_number && <span className="text-red-500">*</span>}</Label>
            <Input data-testid={`order-tracking-${o.id}`} value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="e.g. IN1234567AB" className="h-9 text-sm" />
          </div>
          <div>
            <Label className="text-[11px]">Courier / Partner</Label>
            <Input data-testid={`order-courier-${o.id}`} value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="e.g. Delhivery" className="h-9 text-sm" />
          </div>
          <div className="flex items-end">
            <Button data-testid={`order-save-tracking-${o.id}`} onClick={saveTracking} disabled={savingTracking} className="w-full h-9 rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white text-xs">
              {savingTracking ? "Saving…" : "Save Tracking"}
            </Button>
          </div>
        </div>
      )}

      <ShipmentControls order={o} vendorId={user?.id} onChange={onReload} />

      {(o.status_history || []).length > 0 && (
        <details className="mt-4" data-testid={`order-history-${o.id}`}>
          <summary className="text-xs text-gray-500 cursor-pointer hover:text-[#F48E2E]">Status history ({o.status_history.length})</summary>
          <div className="mt-2 space-y-1 pl-3 border-l-2 border-[#F48E2E]/30">
            {o.status_history.slice().reverse().map((h, i) => (
              <div key={i} className="text-xs">
                <span className="font-medium capitalize">{h.status}</span>
                {h.tracking_number && <span className="text-gray-600"> · tracking {h.tracking_number}</span>}
                {h.courier && <span className="text-gray-600"> · {h.courier}</span>}
                <span className="text-gray-400 ml-2">{new Date(h.updated_at).toLocaleString("en-IN")}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
